import { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { signInWithGoogle } from '@/lib/supabase';
import { useAuth } from '@/lib/auth-guard';
import { useCompanyBranding } from '@/lib/queries';
import { ThemeToggle } from '@/components/ThemeToggle';

export function LoginRoute() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, teamMember, loading } = useAuth();
  const [params] = useSearchParams();
  const reason = params.get('reason');

  // Org logo (migration 044). Anonymous SELECT is permitted via 045 because
  // the data is non-sensitive (the file is in a public bucket). Falls back
  // to the text title when no logo is set.
  const brandingQ = useCompanyBranding();
  const logoUrl = brandingQ.data?.logo_url ?? null;

  // Where to send the user after sign-in. Falls back to /my-day (home).
  const fromState = (location.state as { from?: string } | null)?.from;
  const redirectTo = fromState && fromState !== '/login' ? fromState : '/my-day';

  useEffect(() => {
    if (!loading && session && teamMember) {
      navigate(redirectTo, { replace: true });
    }
  }, [loading, session, teamMember, navigate, redirectTo]);

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-muted/30 p-4">
      {/* Sign-in is the only surface an unauthenticated visitor can reach, so
          without this there's no way to change the theme — or even discover
          that a dark one exists — before logging in. */}
      <ThemeToggle variant="compact" className="absolute right-4 top-4" />
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Company logo"
              // Uploaded logos are authored for a light background — this one
              // is dark wordmark text on transparency, which all but vanishes
              // in dark mode. A light plate keeps the brand colours exact
              // (a CSS invert would turn the red mark grey) and costs nothing
              // in light mode, where the plate is the same colour as the card.
              className="mb-3 h-12 w-auto max-w-[220px] object-contain dark:rounded-md dark:bg-white dark:p-2"
            />
          ) : null}
          <CardTitle>PA Support Tickets</CardTitle>
          <CardDescription>
            Restricted to Practice Acceleration / HBC Group team members.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          {reason ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              {reason}
            </div>
          ) : null}
          <Button
            onClick={() => {
              void signInWithGoogle();
            }}
          >
            Sign in with Google
          </Button>
          {/* Named a Postgres table to the person signing in. This is the very
              first screen anyone sees, including a new hire on day one. */}
          <p className="text-xs text-muted-foreground">
            You'll need to be an active member of the support team. If sign-in is
            refused, ask an admin to add you.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
